/**
 * Dynamic French Translation Engine — Shopify Theme
 *
 * Strategy (NO MutationObserver):
 *  1. Initial full-page translate on DOMContentLoaded
 *  2. IntersectionObserver  — translates sections as they scroll into view
 *  3. shopify:section:load  — Shopify's official event for dynamically loaded sections
 *  4. Theme custom events   — cart:updated, drawer:open, quickview:open, etc.
 *  5. requestAnimationFrame periodic scan — ultra-lightweight fallback that
 *     auto-pauses in background tabs and never blocks the main thread
 *
 * Translation backend: Google Translate free endpoint (no API key required)
 * Caching: sessionStorage — instant on same-session page visits
 */

(function () {
  'use strict';

  /* ─── 1. Guard — run for French locale OR theme preview mode ─────── */
  var locale = (window.shopLocale || document.documentElement.lang || '').toLowerCase();
  var isFrench = locale.startsWith('fr');
  var isPreview = window.shopDesignMode === true || window.Shopify && window.Shopify.designMode;
  if (!isFrench && !isPreview) return;
  // In preview mode with non-French locale, still translate so merchant sees it
  if (isPreview && !isFrench) {
    window.shopLocale = 'fr';
  }

  /* ─── 2. Manual overrides from theme customizer ──────────────────────
     Admin enters: English text | French text  (one per line)
     These always win over Google Translate.                            */
  var MANUAL = {};
  try {
    var raw_overrides = (window.shopTranslationOverrides || '');
    // Handle both \n and \r\n line endings
    raw_overrides.split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line) return;
      // Use indexOf so French text can contain | safely
      var sep = line.indexOf('|');
      if (sep === -1) return;
      var en = line.substring(0, sep).trim();
      var fr = line.substring(sep + 1).trim();
      if (en && fr) MANUAL[en] = fr;
    });
  } catch (e) {}

  /* ─── 3. Translation cache (localStorage — persistent across sessions) ── */
  var CACHE_KEY = 'dt_fr_v5';
  var cache = {};

  try {
    var raw = localStorage.getItem(CACHE_KEY);
    if (raw) cache = JSON.parse(raw);
  } catch (e) {}

  // If overrides changed (add/edit/remove), wipe entire cache so everything re-translates fresh
  var MANUAL_HASH_KEY = 'dt_fr_manual_hash';
  var currentHash = JSON.stringify(MANUAL);
  var prevHash = '';
  try { prevHash = localStorage.getItem(MANUAL_HASH_KEY) || ''; } catch(e) {}

  if (currentHash !== prevHash) {
    cache = {};
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.setItem(MANUAL_HASH_KEY, currentHash);
    } catch(e) {}
  }

  // Apply current MANUAL overrides into cache
  Object.keys(MANUAL).forEach(function (k) { cache[k] = MANUAL[k]; });
  if (Object.keys(MANUAL).length) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch(e) {}
  }

  function saveCache() {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  /* ─── 4. Show page (called after cache translations applied) ─────────── */
  function showPage() {
    if (window.__showPageTimer) clearTimeout(window.__showPageTimer);
    if (window.__showPage) window.__showPage();
  }

  /* ─── 3. Helpers ──────────────────────────────────────────────────── */
  function isTranslatable(str) {
    if (!str) return false;
    str = str.trim();
    if (str.length < 2) return false;
    // Skip pure numbers / prices / symbols / codes
    if (/^[\d\s.,\$£€%+\-\/:&#@!^~|<>()\[\]{}]+$/.test(str)) return false;
    // Skip CSS-like text (e.g. ".a{fill:none;stroke:#000}") — these leak in from inline SVG <style>
    if (/[{}]/.test(str) && /[a-z-]+\s*:\s*[^;{}]+[;}]/i.test(str)) return false;
    // Skip content that contains embedded newlines — breaks batch line-alignment
    if (/[\r\n]/.test(str)) return false;
    return true;
  }

  /* ─── 4. Google Translate free API ───────────────────────────────── */
  // Correct response structure: data[0] = array of [translatedChunk, originalChunk, ...]
  // We join chunks to get full translation. data[0][0][0] = first chunk only.
  async function callGoogleTranslate(text) {
    var url =
      'https://translate.googleapis.com/translate_a/single' +
      '?client=gtx&sl=en&tl=fr&dt=t&q=' +
      encodeURIComponent(text);
    try {
      var resp = await fetch(url);
      if (!resp.ok) return text;
      var data = await resp.json();
      // Concatenate all translated segments
      var result = '';
      if (data && data[0]) {
        data[0].forEach(function (seg) {
          if (seg && seg[0]) result += seg[0];
        });
      }
      return result || text;
    } catch (e) {
      return text; // silently keep original on network error
    }
  }

  /* ─── 5. Batch translator ─────────────────────────────────────────── */
  // Joins texts with \n, sends one request, splits result by \n
  // Chunks: max 40 items or 3000 chars per request
  async function translateBatch(rawTexts) {
    // Deduplicate and filter
    var toFetch = [];
    var seen = {};
    rawTexts.forEach(function (t) {
      var key = (t || '').trim();
      if (key && !seen[key] && !cache[key] && isTranslatable(key)) {
        toFetch.push(key);
        seen[key] = true;
      }
    });
    if (!toFetch.length) return;

    // Split into safe chunks
    var chunks = [];
    var chunk = [];
    var chunkLen = 0;
    toFetch.forEach(function (text) {
      if (chunk.length >= 40 || chunkLen + text.length > 3000) {
        if (chunk.length) chunks.push(chunk);
        chunk = [text];
        chunkLen = text.length;
      } else {
        chunk.push(text);
        chunkLen += text.length + 1; // +1 for \n separator
      }
    });
    if (chunk.length) chunks.push(chunk);

    // Fetch each chunk and map back
    for (var i = 0; i < chunks.length; i++) {
      var originals = chunks[i];
      var joined    = originals.join('\n');
      var translated = await callGoogleTranslate(joined);
      var lines      = translated.split('\n');

      // If line counts don't match, alignment is broken — fall back to per-item translation
      // to avoid mis-mapping (e.g. "Add to Cart" getting the translation of a CSS string).
      if (lines.length !== originals.length) {
        for (var j = 0; j < originals.length; j++) {
          var orig = originals[j];
          var solo = await callGoogleTranslate(orig);
          solo = (solo || '').trim();
          if (solo && solo !== orig) cache[orig] = solo;
        }
      } else {
        originals.forEach(function (orig, idx) {
          var tr = (lines[idx] || '').trim();
          if (tr && tr !== orig) cache[orig] = tr;
        });
      }
    }

    saveCache();
  }

  /* ─── 6. DOM text-node collection ────────────────────────────────── */
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, IFRAME: 1,
    SVG: 1, PATH: 1, CIRCLE: 1, RECT: 1, POLYGON: 1, LINE: 1,
    INPUT: 1, TEXTAREA: 1, SELECT: 1, OPTION: 1,
    CODE: 1, PRE: 1, MATH: 1
  };

  function collectTextNodes(root, out) {
    out = out || [];
    if (!root) return out;

    if (root.nodeType === 3) {
      // Raw text node
      var t = root.textContent.trim();
      if (isTranslatable(t)) out.push(root);
      return out;
    }

    if (root.nodeType !== 1) return out;
    // tagName is uppercase for HTML elements but lowercase for inline SVG/MathML — normalize
    var tn = (root.tagName || '').toUpperCase();
    if (SKIP_TAGS[tn]) return out;
    if (root.hasAttribute && root.hasAttribute('data-no-translate')) return out;

    for (var i = 0; i < root.childNodes.length; i++) {
      collectTextNodes(root.childNodes[i], out);
    }
    return out;
  }

  /* ─── 7. Apply translations to collected text nodes ──────────────── */
  async function applyToTextNodes(nodes) {
    if (!nodes.length) return;
    var texts = nodes.map(function (n) { return n.textContent.trim(); });
    await translateBatch(texts);

    nodes.forEach(function (node) {
      var key = node.textContent.trim();
      var tr  = cache[key];
      if (tr && tr !== key) {
        // Preserve surrounding whitespace exactly
        var leading  = node.textContent.match(/^\s*/)[0];
        var trailing = node.textContent.match(/\s*$/)[0];
        node.textContent = leading + tr + trailing;
      }
    });
  }

  /* ─── 8. Translate element attributes ────────────────────────────── */
  var TRANSLATABLE_ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];

  async function applyToAttributes(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    var selector = TRANSLATABLE_ATTRS.map(function (a) { return '[' + a + ']'; }).join(',');
    var els = root.querySelectorAll(selector);
    if (!els.length) return;

    var texts = [];
    els.forEach(function (el) {
      TRANSLATABLE_ATTRS.forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (v && v.trim()) texts.push(v.trim());
      });
    });

    await translateBatch(texts);

    els.forEach(function (el) {
      TRANSLATABLE_ATTRS.forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (v && cache[v.trim()]) el.setAttribute(attr, cache[v.trim()]);
      });
    });
  }

  /* ─── 9. Main subtree translator ─────────────────────────────────── */
  // translatedSet tracks which roots we've already processed so we don't repeat
  var translatedSet = new WeakSet();

  // Some containers update frequently (cart drawer, search) — never mark them done
  var ALWAYS_RETRANSLATE_SELECTORS = [
    '.cart-drawer',
    '.cart-notification',
    '#cart-drawer',
    '.predictive-search',
    '.search-modal',
    '#shopify-section-cart',
    '[data-section-type="cart"]'
  ];

  function isAlwaysRetranslate(el) {
    return ALWAYS_RETRANSLATE_SELECTORS.some(function (sel) {
      try { return el.matches(sel) || el.closest(sel); } catch (e) { return false; }
    });
  }

  async function translateSubtree(root) {
    if (!root) return;
    var alwaysRedo = isAlwaysRetranslate(root);
    if (!alwaysRedo && translatedSet.has(root)) return; // already done
    if (!alwaysRedo) translatedSet.add(root);

    var nodes = collectTextNodes(root);
    await applyToTextNodes(nodes);
    await applyToAttributes(root);
  }

  /* ─────────────────────────────────────────────────────────────────
     ALTERNATIVES TO MUTATIONOBSERVER
  ───────────────────────────────────────────────────────────────── */

  /* ─── 10. IntersectionObserver ────────────────────────────────────
     Translates sections / containers as they scroll into the viewport.
     rootMargin:'300px' pre-translates content just before it's visible. */
  var intersectionObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        translateSubtree(entry.target);
        if (!isAlwaysRetranslate(entry.target)) {
          intersectionObserver.unobserve(entry.target); // observe once
        }
      }
    });
  }, { rootMargin: '300px 0px', threshold: 0 });

  function observeWithIntersection(root) {
    root = root || document;
    var els = root.querySelectorAll(
      '.shopify-section, [data-section-id], section, .card-wrapper, ' +
      '.product-card-wrapper, .blog-articles, .featured-collection, ' +
      '.cart-drawer, .cart-notification, .predictive-search, .search-modal'
    );
    els.forEach(function (el) { intersectionObserver.observe(el); });
  }

  /* ─── 11. Shopify official section lifecycle events ───────────────
     shopify:section:load fires every time Shopify dynamically loads
     a section (theme editor previews, AJAX section rendering calls).  */
  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target || (event.detail && event.detail.sectionEl) || document.body;
    translateSubtree(el);
    observeWithIntersection(el);
  });

  /* ─── 12. Shopify theme editor events ─────────────────────────────
     These fire in the theme customizer when blocks are selected/reordered. */
  ['shopify:block:select', 'shopify:section:select', 'shopify:section:reorder'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      setTimeout(function () {
        translateSubtree(e.target || document.body);
      }, 150);
    });
  });

  /* ─── 13. Common Shopify theme custom events ──────────────────────
     Different themes fire these on cart update, drawer open, quickview, etc.
     We listen on document with event delegation (zero cost when not fired). */
  var THEME_EVENTS = [
    // Cart events
    'cart:updated', 'cart:item-added', 'cart:refresh',
    'theme:cart:open', 'ajaxcart:itemAdded', 'cartdrawer:open',
    // Drawer / modal
    'drawer:open', 'modal:open', 'modal:opened',
    // Quickview
    'quickview:open', 'quickview:loaded', 'quick-add:success',
    // Search
    'search:results-loaded', 'predictive-search:results',
    // Product / recommendations
    'recommendations:loaded', 'product:loaded', 'variant:changed',
    // PageFly / GemPages custom events
    'pfcustom:sectionLoaded', 'gp:sectionLoaded'
  ];

  THEME_EVENTS.forEach(function (evtName) {
    document.addEventListener(evtName, function (e) {
      var target =
        (e.detail && (e.detail.el || e.detail.container)) ||
        e.target ||
        document.body;
      // Slight delay so the theme has time to finish injecting HTML
      setTimeout(function () { translateSubtree(target); }, 150);
    });
  });

  /* ─── 14. requestAnimationFrame periodic scan ─────────────────────
     Lightweight fallback that catches anything the events above missed.
     Runs at ~1 check per 3 seconds. Automatically pauses when the tab
     is in the background (browser suspends rAF for hidden tabs).
     Does NOT block the main thread — each frame just checks a timestamp. */
  var lastScan    = 0;
  var SCAN_EVERY  = 3000; // ms between scans

  // Selectors for containers that update dynamically but may not fire events
  var DYNAMIC_SELECTORS = [
    '.cart-drawer', '#cart-drawer', '.cart-notification',
    '.predictive-search__results', '.search-modal__content',
    '.quickview-modal', '#quick-add-modal',
    '.recently-viewed', '#shopify-section-predictive-search'
  ].join(',');

  function periodicScan(timestamp) {
    if (timestamp - lastScan >= SCAN_EVERY) {
      lastScan = timestamp;

      // Only scan the dynamic containers — not full body every 3s
      var dynamicEls = document.querySelectorAll(DYNAMIC_SELECTORS);
      dynamicEls.forEach(function (el) {
        translateSubtree(el); // isAlwaysRetranslate handles repeats
      });
    }
    requestAnimationFrame(periodicScan);
  }

  /* ─── 15. Boot ────────────────────────────────────────────────────── */
  function applyCacheSync(root) {
    // Apply already-cached translations synchronously (no API call)
    // so page shows in French immediately on cached visits
    var nodes = collectTextNodes(root);
    nodes.forEach(function (node) {
      var key = node.textContent.trim();
      var tr  = cache[key];
      if (tr && tr !== key) {
        var leading  = node.textContent.match(/^\s*/)[0];
        var trailing = node.textContent.match(/\s*$/)[0];
        node.textContent = leading + tr + trailing;
      }
    });
    var ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];
    var selector = ATTRS.map(function(a){ return '['+a+']'; }).join(',');
    root.querySelectorAll(selector).forEach(function(el) {
      ATTRS.forEach(function(attr) {
        var v = el.getAttribute(attr);
        if (v && cache[v.trim()]) el.setAttribute(attr, cache[v.trim()]);
      });
    });
  }

  function boot() {
    // Step 1 — apply cached translations synchronously (zero API delay)
    applyCacheSync(document.body);

    // Step 2 — show page now (cached translations already applied)
    showPage();

    // Step 3 — fetch any missing translations from API async (background)
    translateSubtree(document.body).then(function () {
      // Step 4 — set up IntersectionObserver for sections
      observeWithIntersection(document);

      // Step 5 — start periodic rAF scan for dynamic areas
      requestAnimationFrame(periodicScan);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
